import { Link } from 'react-router-dom';
import { GraduationCap, FileCheck, Banknote, ArrowRight, CheckCircle, Target } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';

const ThreeFPage = () => {
  const threeFs = [
    {
      letter: 'F',
      number: '1',
      title: 'Formation',
      subtitle: 'Acquérir les compétences essentielles',
      description: 'La première étape vers le succès entrepreneurial est l\'acquisition des connaissances et compétences nécessaires pour bien structurer votre activité.',
      icon: GraduationCap,
      color: 'bg-[#006F33]',
      features: [
        'Fondamentaux du droit des affaires',
        'Gestion financière et comptabilité',
        'Marketing et développement commercial',
        'Leadership et management d\'équipe',
        'Stratégie et business plan'
      ],
      benefits: [
        'Confiance dans vos décisions',
        'Réduction des erreurs coûteuses',
        'Meilleure préparation aux défis'
      ]
    },
    {
      letter: 'F',
      number: '2',
      title: 'Formalisation',
      subtitle: 'Sécuriser juridiquement votre projet',
      description: 'La formalisation est l\'étape cruciale qui transforme votre idée en une entité juridique solide et protégée.',
      icon: FileCheck,
      color: 'bg-[#E6C200]',
      textDark: true,
      features: [
        'Choix de la forme juridique optimale',
        'Rédaction des statuts et documents',
        'Immatriculation et formalités administratives',
        'Protection de la propriété intellectuelle',
        'Mise en conformité réglementaire'
      ],
      benefits: [
        'Base légale solide',
        'Protection des associés',
        'Crédibilité renforcée'
      ]
    },
    {
      letter: 'F',
      number: '3',
      title: 'Financement',
      subtitle: 'Trouver les ressources pour grandir',
      description: 'Le financement est le carburant de votre croissance. Nous vous aidons à identifier et obtenir les meilleures options pour soutenir votre développement.',
      icon: Banknote,
      color: 'bg-[#C0392B]',
      features: [
        'Élaboration du business plan financier',
        'Identification des sources de financement',
        'Préparation des dossiers de demande',
        'Accompagnement auprès des investisseurs',
        'Négociation des conditions'
      ],
      benefits: [
        'Accès aux capitaux nécessaires',
        'Conditions optimisées',
        'Croissance accélérée'
      ]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero" data-testid="threef-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Notre Méthode</span>
            <h1 className="hero-title mt-4" data-testid="threef-title">
              Les <span className="text-[#006F33]">3F</span> du Succès
            </h1>
            <p className="hero-subtitle mt-6 max-w-2xl mx-auto">
              Une méthodologie unique et éprouvée pour transformer votre projet entrepreneurial 
              en une entreprise prospère : Formation, Formalisation, Financement.
            </p>
          </div>
          
          {/* Visual representation */}
          <div className="mt-16 flex justify-center items-center gap-4 md:gap-8">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[#006F33] flex items-center justify-center shadow-lg">
                <GraduationCap className="w-10 h-10 md:w-12 md:h-12 text-white" />
              </div>
              <span className="mt-3 font-semibold text-[#006F33]">Formation</span>
            </div>
            <ArrowRight className="w-6 h-6 md:w-8 md:h-8 text-slate-300" />
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[#E6C200] flex items-center justify-center shadow-lg">
                <FileCheck className="w-10 h-10 md:w-12 md:h-12 text-[#1A1A1A]" />
              </div>
              <span className="mt-3 font-semibold text-[#B39500]">Formalisation</span>
            </div>
            <ArrowRight className="w-6 h-6 md:w-8 md:h-8 text-slate-300" />
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[#C0392B] flex items-center justify-center shadow-lg">
                <Banknote className="w-10 h-10 md:w-12 md:h-12 text-white" />
              </div>
              <span className="mt-3 font-semibold text-[#C0392B]">Financement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed 3F Sections */}
      {threeFs.map((f, index) => (
        <section 
          key={index}
          className={`py-20 md:py-32 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F9F9F7]'}`}
          data-testid={`threef-detail-${f.title.toLowerCase()}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl ${f.color} flex items-center justify-center shadow-lg`}>
                    <f.icon className={`w-8 h-8 ${f.textDark ? 'text-[#1A1A1A]' : 'text-white'}`} />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Étape {f.number}</span>
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]">{f.title}</h2>
                  </div>
                </div>
                
                <p className="text-lg text-[#006F33] font-medium mb-4">{f.subtitle}</p>
                <p className="text-slate-600 leading-relaxed mb-8">{f.description}</p>
                
                <div className="space-y-8">
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-4">Ce que nous proposons :</h4>
                    <ul className="space-y-3">
                      {f.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-4">Bénéfices :</h4>
                    <div className="flex flex-wrap gap-3">
                      {f.benefits.map((benefit, bIndex) => (
                        <span 
                          key={bIndex}
                          className={`px-4 py-2 rounded-full text-sm font-medium ${f.color} ${f.textDark ? 'text-[#1A1A1A]/80' : 'text-white/90'}`}
                        >
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Visual */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className={`${f.color} rounded-3xl p-12 ${f.textDark ? 'text-[#1A1A1A]' : 'text-white'}`}>
                  <div className="text-[120px] md:text-[180px] font-bold opacity-20 leading-none">
                    {f.letter}<sub className="text-6xl md:text-8xl">{f.number}</sub>
                  </div>
                  <div className={`absolute bottom-8 right-8 ${f.textDark ? 'text-[#1A1A1A]/60' : 'text-white/60'}`}>
                    <f.icon className="w-20 h-20 md:w-32 md:h-32" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Why This Method */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-method">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Pourquoi les 3F ?</span>
            <h2 className="section-title mt-4">Une approche unique et complète</h2>
            <p className="section-subtitle mt-4 max-w-2xl mx-auto">
              Peu de structures offrent un parcours aussi complet, qui va de l'idée à la concrétisation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl border border-slate-100" data-testid="method-benefit-1">
              <div className="w-16 h-16 rounded-full bg-[#006F33]/10 flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-[#006F33]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Parcours Structuré</h3>
              <p className="text-slate-600">Un chemin clair avec des étapes définies pour transformer votre projet en succès.</p>
            </div>
            
            <div className="text-center p-8 rounded-2xl border border-slate-100" data-testid="method-benefit-2">
              <div className="w-16 h-16 rounded-full bg-[#E6C200]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#B39500]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Approche Globale</h3>
              <p className="text-slate-600">Formation, aspects juridiques et financement traités dans un seul parcours intégré.</p>
            </div>
            
            <div className="text-center p-8 rounded-2xl border border-slate-100" data-testid="method-benefit-3">
              <div className="w-16 h-16 rounded-full bg-[#C0392B]/10 flex items-center justify-center mx-auto mb-6">
                <ArrowRight className="w-8 h-8 text-[#C0392B]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Résultats Concrets</h3>
              <p className="text-slate-600">Une méthode éprouvée qui a accompagné plus de 200 entreprises vers le succès.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#006F33]" data-testid="threef-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Prêt à suivre le parcours des 3F ?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Contactez-nous pour une consultation gratuite et découvrez comment les 3F peuvent transformer votre projet.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#006F33] hover:bg-slate-100 shadow-lg px-8 py-3 rounded-full font-medium flex items-center gap-2 mx-auto" data-testid="threef-cta-button">
              Commencer mon parcours
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ThreeFPage;
